export interface CustomerConfig {
  brand_voice?: string
  min_validation_score?: number
  response_length?: 'short' | 'medium' | 'detailed'
  auto_respond_positive?: boolean
  auto_respond_neutral?: boolean
  auto_respond_negative?: boolean
  full_name?: string
  example_responses?: string[]
}

export type ReviewStatus =
  | 'pending'
  | 'processing'
  | 'pending_approval'
  | 'responded'
  | 'escalated'
  | 'failed'
  | 'skipped'

export type Platform = 'all' | 'google' | 'judgeme'

export type ActivePage = 'dashboard' | 'settings' | 'escalations' | 'inbox' | 'analytics'

export interface IntentAnalysis {
  sentiment: 'positive' | 'neutral' | 'negative'
  urgency?: 'low' | 'medium' | 'high'
  topics?: string[]
}

export interface EscalationResult {
  severity?: 'low' | 'medium' | 'high'
  should_escalate?: boolean
  /** False when a review escalated only because the AI's draft reply didn't
   * pass quality validation, not because the review itself is negative (e.g.
   * a 5-star review with no text, whose auto-generated reply came out too
   * short). Missing/undefined is treated as true (urgent) — same default the
   * backend uses when an older record predates this field. */
  is_negative_review?: boolean
  customer_reason_summary?: string | null
}

export interface DashboardReview {
  id: string
  location_id: string
  platform: string
  platform_review_id: string | null
  google_review_id: string | null
  reviewer_name: string | null
  review_text: string | null
  rating: number | null
  status: ReviewStatus
  review_date: string | null
  generated_response: string | null
  intent_analysis: IntentAnalysis | null
  escalation_result: EscalationResult | null
  response_posted: boolean
  response_posted_at: string | null
  escalated_to_human: boolean
  created_at: string
  processed_at: string | null
}

/** Where the review data originated — determines dashboard banner. */
export type DataSource = 'demo' | 'serpapi' | 'live'

export function deriveDataSource(review: DashboardReview): DataSource {
  const id = review.platform_review_id ?? review.google_review_id
  if (id?.startsWith('demo_')) return 'demo'
  if (id?.startsWith('serpapi_')) return 'serpapi'
  return 'live'
}

/**
 * Compute the overall data-source state for the reviews list.
 * Returns null when all data is fully live (no banner needed).
 */
export function computeDataSourceState(
  reviews: DashboardReview[],
): 'all_demo' | 'has_serpapi' | null {
  if (reviews.length === 0) return null
  const sources = reviews.map(deriveDataSource)
  const hasLive = sources.includes('live')
  if (hasLive) return null
  const hasSerpapi = sources.includes('serpapi')
  if (hasSerpapi) return 'has_serpapi'
  return 'all_demo'
}

export interface ReviewStats {
  total_reviews: number
  by_status: Record<ReviewStatus, number>
  average_rating: number
  response_rate_percent: number
  escalation_rate_percent: number
  total_responded: number
  total_escalated: number
}

export interface AnalyticsDailyPoint {
  date: string
  review_count: number
  avg_rating: number | null
}

export interface AnalyticsValidationPoint {
  date: string
  avg_validation_score: number
}

export interface AnalyticsEscalationReason {
  reason: string
  count: number
}

export interface AnalyticsResponseQuality {
  category: string
  count: number
}

export interface AnalyticsOverview {
  period_days: number
  summary: {
    total_reviews: number
    avg_rating: number
    response_rate_percent: number
    escalation_rate_percent: number
    avg_validation_score: number
  }
  daily_trend: AnalyticsDailyPoint[]
  validation_trend: AnalyticsValidationPoint[]
  escalation_reasons: AnalyticsEscalationReason[]
  response_quality: AnalyticsResponseQuality[]
  min_validation_score: number
}

/** Escalated for a genuine reason (low rating, escalation keywords, high
 * urgency, negative sentiment) — as opposed to a quality-only escalation on
 * an otherwise fine review. Drives dashboard pinning/badge treatment so a
 * 5-star review whose AI draft just came out too short doesn't get the same
 * "needs urgent attention" pin and red styling as a real complaint. */
export function isUrgentEscalation(review: DashboardReview): boolean {
  return review.status === 'escalated' && review.escalation_result?.is_negative_review !== false
}

export function isQualityOnlyEscalation(review: DashboardReview): boolean {
  return review.status === 'escalated' && review.escalation_result?.is_negative_review === false
}

export type WorkflowTag = 'Auto' | 'Approval' | 'Manual' | 'Edited'

export function deriveWorkflowTag(review: DashboardReview): WorkflowTag {
  if (review.escalated_to_human) return 'Manual'
  if (review.status === 'pending_approval') return 'Approval'
  if (review.response_posted) return 'Auto'
  return 'Approval'
}
